<template>
  <div>
    <b-form>
      <b-row>
        <b-col xl="3">
          <h5>{{ $t("message.serviceDetails") }}</h5>
        </b-col>
        <b-col xl="6">
          <ValidationProvider
            :name="$t('message.serviceName')"
            rules="required|min:1"
            v-slot="{ errors }"
          >
            <fg-input
              :label="$t('message.serviceName')"
              v-model="value.name"
              :placeholder="$t('message.serviceNamePlaceholder')"
              :error="errors[0]"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            :rules="`required|min_value:${minDuration}`"
            v-slot="{ errors }"
          >
            <fg-input :label="$t('message.packageDuration')" :error="errors[0]">
              <b-input-group>
                <template v-slot:prepend>
                  <b-dropdown
                    style="width: 7em"
                    toggle-class="m-0"
                    :text="value.durationUnit | toDurationUnitLabel"
                    variant="info"
                    aria-role="form-control"
                  >
                    <b-dropdown-item-button
                      v-for="option in durationUnitOptions"
                      :key="`duration__${option.value}`"
                      :active="value.durationUnit === option.value"
                      @click="value.durationUnit = option.value"
                    >
                      {{ option.label }}
                    </b-dropdown-item-button>
                  </b-dropdown>
                </template>
                <b-form-input
                  v-model.number="value.duration"
                  type="number"
                  :min="minDuration"
                  :placeholder="$t('message.packageDurationPlaceholder')"
                >
                </b-form-input>
              </b-input-group>
            </fg-input>
          </ValidationProvider>
          <ValidationProvider
            :rules="`required|min_value:${minDisplayDuration}`"
            v-slot="{ errors }"
          >
            <fg-input
              :label="$t('message.packageDisplayDuration')"
              :error="errors[0]"
            >
              <b-input-group>
                <template v-slot:prepend>
                  <b-dropdown
                    style="width: 7em"
                    toggle-class="m-0"
                    :text="value.displayDurationUnit | toDurationUnitLabel"
                    variant="info"
                    aria-role="form-control"
                  >
                    <b-dropdown-item-button
                      v-for="option in durationUnitOptions"
                      :key="`displayDuration__${option.value}`"
                      :active="value.displayDurationUnit === option.value"
                      @click="value.displayDurationUnit = option.value"
                    >
                      {{ option.label }}
                    </b-dropdown-item-button>
                  </b-dropdown>
                </template>
                <b-form-input
                  v-model.number="value.displayDuration"
                  type="number"
                  :min="minDisplayDuration"
                  :placeholder="$t('message.serviceDisplayDuration')"
                >
                </b-form-input>
              </b-input-group>
            </fg-input>
          </ValidationProvider>
          <fg-input
            :label="$t('message.serviceDescription')"
            v-model="value.description"
            :placeholder="$t('message.serviceDescriptionPlaceholder')"
          />
          <fg-input class="mt-4">
            <n-checkbox v-model="value.availableForOnlineBookings">
              {{ $t("message.serviceAvailableForOnlineBookings") }}
            </n-checkbox>
          </fg-input>
          <div v-if="value.availableForOnlineBookings">
            <fg-input class="mt-4">
              <n-checkbox v-model="value.requiresContact">
                {{ $t("message.serviceRequireContact") }}
              </n-checkbox>
            </fg-input>
            <div v-if="value.requiresContact" class="ml-5">
              <fg-input v-if="!!shop.contactEmail" class="mt-4">
                <n-checkbox v-model="value.requiresContactByEmail">
                  {{ $t("message.serviceRequireContactByEmail") }}
                </n-checkbox>
              </fg-input>
              <fg-input v-if="!!shop.contactNumber" class="mt-4">
                <n-checkbox v-model="value.requiresContactByPhone">
                  {{ $t("message.serviceRequireContactByPhone") }}
                </n-checkbox>
              </fg-input>
            </div>
            <fg-input class="mt-4">
              <n-checkbox v-model="value.hideDurations">
                {{ $t("message.serviceHideDurations") }}
              </n-checkbox>
            </fg-input>
          </div>
        </b-col>
      </b-row>
      <hr />
      <b-row class="mt-4">
        <b-col xl="3">
          <h5>
            {{ $t("message.employees") }}
          </h5>
        </b-col>
        <b-col xl="6">
          <div v-if="isLoadingEmployees" class="text-center">
            <b-spinner variant="primary"></b-spinner>
          </div>
          <div v-else>
            <div v-for="entry in linkedEmployeeEntries" :key="entry.id">
              <b-row class="align-items-center" style="height: 3em">
                <b-col xs="6">
                  <h6 class="m-0" :class="{ 'text-muted': entry.isStale }">
                    {{ entry.name }}
                  </h6>
                </b-col>
                <b-col xs="6" class="text-right">
                  <n-button
                    class="m-0 p-2"
                    link
                    round
                    @click.native="removeEmployee(entry.id)"
                  >
                    <i class="fa fa-remove" />
                  </n-button>
                </b-col>
              </b-row>
              <div class="pt-1">
                <hr />
              </div>
            </div>
            <div class="text-center">
              <n-button
                type="secondary"
                simple
                @click.native="showAddEmployeeModal"
                :disabled="allEmployeesAreAdded"
              >
                <i class="fa fa-plus mr-2" />
                {{ $t("message.addEmployee") }}
              </n-button>
            </div>
          </div>
        </b-col>
      </b-row>
      <div>
        <hr />
        <b-row class="mt-4">
          <b-col xl="3">
            <h5>{{ $t("message.packages") }}</h5>
          </b-col>
          <b-col xl="6">
            <div
              v-for="(pack, index) in packagesAdded"
              :key="`pack__${pack._id}`"
            >
              <b-row class="align-items-center" style="height: 3em">
                <b-col xs="6">
                  <h6 class="m-0">
                    {{
                      index === 0
                        ? $t("message.basePackage")
                        : $t("message.extraPackage", { number: index })
                    }}
                  </h6>
                </b-col>
                <b-col xs="6" class="text-right">
                  <n-button
                    v-if="index > 0"
                    class="m-0 p-2"
                    link
                    round
                    @click.native="removePackage(pack)"
                  >
                    <i class="fa fa-remove" />
                  </n-button>
                </b-col>
              </b-row>
              <ValidationProvider rules="required" v-slot="{ errors }">
                <fg-input
                  :label="$t('message.packageName')"
                  v-model="pack.name"
                  :placeholder="$t('message.packageNamePlaceholder')"
                  :error="errors[0]"
                  required
                />
              </ValidationProvider>
              <fg-input
                :label="$t('message.packageDescription')"
                v-model="pack.description"
                :placeholder="$t('message.packageDescriptionPlaceholder')"
              />

              <ValidationProvider rules="required" v-slot="{ errors }">
                <fg-input
                  :label="$t('message.packagePrice')"
                  :error="errors[0]"
                  required
                >
                  <b-input-group prepend="€">
                    <b-form-input
                      v-model.number="pack.price"
                      type="number"
                      step="0.01"
                      min="0"
                      :placeholder="$t('message.packagePricePlaceholder')"
                    >
                    </b-form-input>
                  </b-input-group>
                </fg-input>
              </ValidationProvider>
              <div class="pt-1">
                <hr />
              </div>
            </div>
            <div class="text-center">
              <n-button type="secondary" simple @click.native="addPackage">
                <i class="fa fa-plus mr-2" />{{ $t("message.addPackage") }}
              </n-button>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-form>
    <modal :show.sync="isAddEmployeeModalVisible">
      <h4 slot="header" class="title title-up">
        {{ $t("message.addEmployee") }}
      </h4>
      <b-list-group flush>
        <b-list-group-item
          v-for="employee in employeesNotYetAdded"
          :key="employee._id"
          button
          @click="addEmployee(employee)"
        >
          {{ employee.name }}
        </b-list-group-item>
      </b-list-group>
    </modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { Checkbox, Modal } from "@/components";
import { DURATION_UNITS, servicePackage } from "./services.lib";
import i18n from "@/i18n";
import { getEmployeeDisplayName } from "@/utils/staleRefGuards";

const durationUnitOptions = [
  {
    value: DURATION_UNITS.MINUTES,
    label: i18n.t("message.minutes"),
  },
  {
    value: DURATION_UNITS.HOURS,
    label: i18n.t("message.hours"),
  },
];

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    [Checkbox.name]: Checkbox,
    Modal,
  },
  props: ["value"],
  filters: {
    toDurationUnitLabel(value) {
      const durationUnitOption = durationUnitOptions.find(
        (option) => option.value === value
      );
      if (!durationUnitOption) return null;
      return durationUnitOption.label;
    },
  },
  data() {
    return {
      isLoadingEmployees: false,
      isLoadingResources: false,
      isAddEmployeeModalVisible: false,
      durationUnitOptions,
      currency: "eur",
    };
  },
  methods: {
    ...mapActions({
      fetchEmployees: "employees/fetchEmployees",
    }),
    async loadEmployees() {
      this.showEmployeesLoadingIndicator();
      try {
        await this.fetchEmployees();
      } catch (e) {
        this.$notify({
          type: "error",
          title: this.$t("message.error"),
          text: e.message,
        });
      } finally {
        this.hideEmployeesLoadingIndicator();
      }
    },
    showEmployeesLoadingIndicator() {
      this.isLoadingEmployees = true;
    },
    hideEmployeesLoadingIndicator() {
      this.isLoadingEmployees = false;
    },
    showAddEmployeeModal() {
      this.isAddEmployeeModalVisible = true;
    },
    hideAddEmployeeModal() {
      this.isAddEmployeeModalVisible = false;
    },
    addEmployee({ _id }) {
      this.value.resources.push(_id);
      this.hideAddEmployeeModal();
    },
    removeEmployee(id) {
      const { resources } = this.value;
      resources.splice(resources.indexOf(id), 1);
    },
    addPackage() {
      const { packages, packagesDisplayOrder } = this.value;
      const lastPackageId =
        packagesDisplayOrder[packagesDisplayOrder.length - 1];
      const { price } = packages[lastPackageId];

      const pack = servicePackage({
        price,
      });

      packages[pack._id] = pack;
      packagesDisplayOrder.push(pack._id);
    },
    removePackage({ _id }) {
      const { packages, packagesDisplayOrder } = this.value;
      delete packages[_id];
      packagesDisplayOrder.splice(packagesDisplayOrder.indexOf(_id), 1);
    },
  },
  computed: {
    ...mapGetters({
      employees: "employees/employees",
      shop: "shops/shop",
    }),
    minDuration() {
      return this.value.durationUnit === DURATION_UNITS.MINUTES ? 5 : 1;
    },
    minDisplayDuration() {
      return this.value.displayDurationUnit === DURATION_UNITS.MINUTES ? 5 : 1;
    },
    employeesNotYetAdded() {
      const { employees } = this;
      const allIds = Object.keys(employees);
      const alreadyAddedIds = this.value.resources;
      const notYetAddedIds = allIds.filter(
        (id) => !alreadyAddedIds.includes(id)
      );
      return notYetAddedIds.map((id) => employees[id]);
    },
    allEmployeesAreAdded() {
      return !this.employeesNotYetAdded.length;
    },
    packagesAdded() {
      const { packages, packagesDisplayOrder } = this.value;
      return packagesDisplayOrder.map((packageId) => packages[packageId]);
    },
    linkedEmployeeEntries() {
      const { employees } = this;
      return (this.value.resources || []).map((employeeId) => ({
        id: employeeId,
        name: getEmployeeDisplayName(employees, employeeId),
        isStale: !employees[employeeId],
      }));
    },
  },
  created() {
    this.loadEmployees();
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
};
</script>
<style scoped>
.el-input .el-select {
  width: 8em;
}
</style>
